import React, { useState, useEffect, useRef } from 'react';
import { Logo } from '../constants.tsx';
import { renderMessageText } from './renderMarkdown.tsx';
import { auth } from '../firebase.ts';

const REMINDER_MESSAGES = [
  "أهلاً بك! أنا سارة، مساعدك بالذكاء الاصطناعي لتحليل أسهمك. أخبرني بسهمك الآن لتجربتي ورؤية التحليل الفوري 🚀",
  "هل تتابع سهماً معيناً؟ شاركني رمزه لأعطيك تحليلاً فورياً لاتجاهه ومستويات الدعم والمقاومة 📊",
  "لا تدع تقلبات السوق تفاجئك. اسألني عن أي سهم سعودي وسأقدم لك قراءة سريعة وفق أحدث البيانات 💡",
  "تبحث عن فرص أو تحليلات؟ يمكنك سؤالي عن تحليل أي شركة في السوق السعودي لتتخذ قرارك بثقة 📈",
  "رأي الذكاء الاصطناعي قد يمنحك زاوية جديدة. جربني الآن واكتب اسم سهمك! 🤖"
];

const generateSessionId = () => {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
};

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'ai';
  isTyping?: boolean;
}

const SaraChatWidget: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [messageIndex, setMessageIndex] = useState(0);
  const [hasShownInitial, setHasShownInitial] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  const scrollToBottomIfNearBottom = () => {
    if (!chatContainerRef.current || !messagesEndRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
    
    // Check if we are within 50px from the bottom
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 50;
    
    if (isNearBottom) {
       messagesEndRef.current.scrollIntoView({ behavior: 'auto' });
    }
  };

  useEffect(() => {
    let timeout: NodeJS.Timeout;

    if (isOpen) {
      setShowPopup(false);
    } else if (showPopup) {
      timeout = setTimeout(() => {
        setShowPopup(false);
        setMessageIndex(prev => (prev + 1) % REMINDER_MESSAGES.length);
      }, 15000);
    } else {
      const delay = !hasShownInitial ? 5000 : 45000;
      timeout = setTimeout(() => {
        setShowPopup(true);
        setHasShownInitial(true);
      }, delay);
    }

    return () => clearTimeout(timeout);
  }, [isOpen, showPopup, hasShownInitial]);

  useEffect(() => {
    let sid = localStorage.getItem('sara_session_id');
    if (!sid) {
      sid = generateSessionId();
      localStorage.setItem('sara_session_id', sid);
    }
    setSessionId(sid);

    // Welcome message
    setMessages([
      { id: 'welcome', text: 'مرحباً! أنا سارة الذكاء الاصطناعي الخاص بتداول. كيف يمكنني مساعدتك في تحليل الأسهم اليوم؟', sender: 'ai' }
    ]);
  }, []);

  useEffect(() => {
    if (messagesEndRef.current) {
      if (!messages[messages.length - 1]?.isTyping) {
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [messages.length, isOpen, isLoading]);

  const handleSend = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userText = inputValue.trim();
    setInputValue('');

    const newUserMsg: Message = { id: Date.now().toString(), text: userText, sender: 'user' };
    const typingMsgId = 'typing-' + Date.now();
    const typingMsg: Message = { id: typingMsgId, text: '', sender: 'ai', isTyping: true };

    setMessages(prev => [...prev, newUserMsg, typingMsg]);
    setIsLoading(true);

    try {
      // Local quota limit for free tier (Ollama backend has no native quota limit)
      const usageKey = `sara_usage_${sessionId}`;
      const currentUsage = parseInt(localStorage.getItem(usageKey) || '0', 10);

      if (currentUsage >= 2) {
        setMessages(prev => prev.filter(m => m.id !== typingMsgId));
        setMessages(prev => [...prev, {
          id: Date.now().toString(),
          text: '⚠️ **لقد استنفدت رصيدك من المحادثات المجانية لهذا اليوم.**\n\nللاستمرار في الحصول على تحليلات متعمقة ومؤشرات فنية بلا حدود، انضم إلى خطتنا المميزة:\n\n🚀 **اشترك الآن واحصل على:**\n• محادثات غير محدودة مع سارة AI\n• تحليلات فنية أعمق ومؤشرات متقدمة للأسهم السعودية\n• قراءات إحصائية لحركة وسيولة السوق\n• أولوية في الرد والدعم الفني\n\n💎 **خيارات الاشتراك:**\n• [🌐 صفحة الاشتراك](https://dash.tad-ai.com/#/pricing)\n\n📞 **تواصل معنا:**\n• البريد الإلكتروني: [admin@tad-ai.com](mailto:admin@tad-ai.com)\n• الدعم الفني: [971-541988034](tel:971-541988034)',
          sender: 'ai'
        }]);
        setIsLoading(false);
        return;
      }

      localStorage.setItem(usageKey, (currentUsage + 1).toString());

      const controller = new AbortController();
      // At least 30 seconds timeout as requested. I'll use 60s to be safe.
      const timeoutId = setTimeout(() => controller.abort(), 60000);

      // Sara is metered per Firebase account, so the widget can only talk to her when signed in.
      const authUser = auth.currentUser;
      let idToken: string | null = null;
      try {
        idToken = authUser && typeof authUser.getIdToken === 'function' ? await authUser.getIdToken() : null;
      } catch (e) {
        idToken = null;
      }
      if (!idToken) {
        clearTimeout(timeoutId);
        localStorage.setItem(usageKey, Math.max(0, currentUsage - 1).toString());
        setMessages(prev => prev.filter(m => m.id !== typingMsgId));
        setMessages(prev => [...prev, {
          id: Date.now().toString(),
          text: '🔒 **لازم تسجّل الدخول الأول** عشان سارة تعرف حسابك وترد عليك.\n\nافتح [صفحة سارة](https://www.tad-ai.com/#/sara) وسجّل الدخول بحسابك، أو اشترك من [صفحة الباقات](https://www.tad-ai.com/#/pricing).',
          sender: 'ai'
        }]);
        setIsLoading(false);
        return;
      }

      // Send to custom Sara AI Node/Python API
      const response = await fetch('https://ai.tad-ai.com/api/sara-chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ message: userText, session_id: sessionId, stream: true }),
        signal: controller.signal
      });
      clearTimeout(timeoutId);

      if (!response.ok) throw new Error('Network error');

      setMessages(prev => prev.filter(m => m.id !== typingMsgId)); // Remove typing

      const reader = response.body?.getReader();
      // stream: true is critical for multi-byte Arabic UTF-8 — prevents incomplete
      // character sequences from being flushed mid-chunk
      const decoder = new TextDecoder("utf-8");
      const aiMsgId = Date.now().toString();
      
      setMessages(prev => [...prev, {
        id: aiMsgId,
        text: '',
        sender: 'ai'
      }]);

      let accumulatedText = '';
      // Holds the tail of the last chunk if it didn't end with \n
      // (prevents two JSON objects landing on the same "line" when TCP
      //  delivers them as one packet without a \n boundary)
      let remainder = '';

      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          
          // Prepend any incomplete line from previous chunk
          const raw = remainder + decoder.decode(value, { stream: true });
          const lines = raw.split('\n');
          // The last element may be an incomplete line — save it for next iteration
          remainder = lines.pop() ?? '';

          for (const line of lines) {
              if (!line.trim()) continue;
              try {
                  const data = JSON.parse(line);
                  if (data.token) {
                      accumulatedText += data.token;
                      setMessages(prev => prev.map(m => m.id === aiMsgId ? { ...m, text: accumulatedText } : m));
                  }
              } catch (e) {
                  console.error("Error parsing stream chunk", e, "line:", line.slice(0, 120));
              }
          }
          scrollToBottomIfNearBottom();
        }

        // Flush any leftover incomplete line after stream ends
        if (remainder.trim()) {
          try {
            const data = JSON.parse(remainder);
            if (data.token) {
              accumulatedText += data.token;
              setMessages(prev => prev.map(m => m.id === aiMsgId ? { ...m, text: accumulatedText } : m));
            }
          } catch (_) { /* ignore truly incomplete tail */ }
        }
      }
    } catch (error) {
      setMessages(prev => prev.filter(m => m.id !== typingMsgId)); // Remove typing
      setMessages(prev => [...prev, {
        id: Date.now().toString(),
        text: 'عذراً، الخادم يستغرق وقتاً طويلاً للرد أو حدث خطأ في الاتصال. يرجى المحاولة لاحقاً.',
        sender: 'ai'
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700&display=swap');
          
          .sara-chat-font {
            font-family: 'Cairo', sans-serif;
          }
          
          .typing-indicator span {
            display: inline-block;
            width: 6px;
            height: 6px;
            background-color: #8B5CF6;
            border-radius: 50%;
            margin: 0 2px;
            animation: bounce 1.4s infinite ease-in-out both;
          }

          .typing-indicator span:nth-child(1) { animation-delay: -0.32s; }
          .typing-indicator span:nth-child(2) { animation-delay: -0.16s; }
          
          @keyframes bounce {
            0%, 80%, 100% { transform: scale(0); }
            40% { transform: scale(1); }
          }
          
          @keyframes slideUpFade {
            0% { opacity: 0; transform: translateY(20px); }
            100% { opacity: 1; transform: translateY(0); }
          }
          .animate-popup {
            animation: slideUpFade 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          }
        `}
      </style>

      {/* Popup Message */}
      {!isOpen && showPopup && (
        <div 
          className="fixed bottom-24 right-6 z-[60] bg-[#0A1021] border border-[#8B5CF6]/50 text-white p-4 rounded-2xl shadow-2xl sara-chat-font animate-popup" 
          dir="rtl" 
          style={{ width: '280px', boxShadow: '0 10px 25px -5px rgba(139, 92, 246, 0.4)' }}
        >
          <button onClick={(e) => { 
            e.stopPropagation(); 
            setShowPopup(false); 
            setMessageIndex(prev => (prev + 1) % REMINDER_MESSAGES.length);
          }} className="absolute top-2 left-2 text-gray-400 hover:text-white transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
          </button>
          <div className="flex items-start gap-3 cursor-pointer" onClick={() => setIsOpen(true)}>
            <div className="flex-shrink-0 mt-1 text-[#8B5CF6]">
               <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09l2.846.813-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z"></path></svg>
            </div>
            <p className="text-sm leading-relaxed m-0 text-gray-100 font-medium pb-1">
               {REMINDER_MESSAGES[messageIndex]}
            </p>
          </div>
          {/* little triangle pointing down to the button */}
          <div className="absolute -bottom-2 w-4 h-4 bg-[#0A1021] border-b border-r border-[#8B5CF6]/50 transform rotate-45" style={{ right: '28px' }}></div>
        </div>
      )}

      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 z-[60] w-14 h-14 rounded-full flex items-center justify-center shadow-2xl transition-transform hover:scale-110 text-white"
        style={{ backgroundColor: '#8B5CF6', boxShadow: '0 4px 20px rgba(139, 92, 246, 0.4)' }}
        aria-label="Chat with Sara AI"
      >
        {isOpen ? (
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
        ) : (
          <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"></path></svg>
        )}
      </button>

      {/* Chat Panel */}
      {isOpen && (
        <div
          className="fixed bottom-24 right-6 z-[60] flex flex-col bg-[#0A1021] border border-[#8B5CF6]/30 rounded-2xl shadow-2xl overflow-hidden sara-chat-font"
          style={{ width: '380px', height: '520px', maxWidth: 'calc(100vw - 48px)', maxHeight: 'calc(100vh - 120px)' }}
          dir="rtl"
        >
          {/* Header */}
          <div className="flex items-center gap-3 p-4 bg-[#0A1021] border-b border-[#8B5CF6]/20 shadow-sm">
            <div className="flex items-center justify-center animate-pulse text-[#8B5CF6] scale-75 transform origin-right">
              <Logo />
            </div>
            <div>
              <h3 className="text-white font-bold text-base m-0">سارة (Sara AI)</h3>
              <p className="text-[#C084FC] text-xs m-0">متصل (محلل السوق السعودي)</p>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="mr-auto text-gray-400 hover:text-white"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
            </button>
          </div>

          {/* App Download Banner */}
          <div className="px-3 py-2 bg-gradient-to-r from-[#8B5CF6]/25 via-indigo-600/20 to-emerald-600/20 border-b border-[#8B5CF6]/30 flex items-center justify-between gap-2 shrink-0">
            <div className="flex items-center gap-1.5 min-w-0">
              <span className="text-sm shrink-0">🤖</span>
              <span className="text-[11px] font-bold text-white truncate">تطبيق سارة الذكي (أندرويد)</span>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <a
                href="https://www.tad-ai.com/SARA-AI.apk"
                target="_blank"
                rel="noopener noreferrer"
                download
                className="inline-flex items-center gap-1 px-2.5 py-1 bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-500 hover:to-purple-500 text-white text-[10px] font-bold rounded-lg shadow transition-all shrink-0"
              >
                <span>APK</span>
              </a>
              <a
                href="https://www.tad-ai.com/TadAI-Setup.exe"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 px-2 py-1 bg-white/10 hover:bg-white/20 text-white text-[10px] font-bold rounded-lg transition-all shrink-0"
              >
                <span>💻 ويندوز</span>
              </a>
            </div>
          </div>

          {/* Messages */}
          <div ref={chatContainerRef} className="flex-1 overflow-y-auto p-4 space-y-4 bg-[#0A1021] relative" style={{ scrollbarWidth: 'thin', scrollbarColor: '#8B5CF6 #0A1021' }}>
            {messages.map((msg) => (
              <div key={msg.id} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[85%] p-3 rounded-2xl text-sm leading-relaxed ${msg.sender === 'user'
                      ? 'bg-[#8B5CF6] text-white rounded-tl-none font-bold'
                      : 'bg-slate-800 text-white rounded-tr-none border border-slate-700'
                    }`}
                >
                  {msg.isTyping ? (
                    <div className="typing-indicator flex items-center h-5 py-1">
                      <span></span><span></span><span></span>
                    </div>
                  ) : (
                    renderMessageText(msg.text)
                  )}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="p-3 border-t border-[#8B5CF6]/20 bg-[#0A1021]">
            <div className="flex items-center bg-slate-800/80 border border-slate-700/80 focus-within:border-[#8B5CF6] rounded-xl px-2 py-1.5 transition-colors shadow-inner">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value.slice(0, 500))}
                onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                placeholder="اسأل سارة عن أي سهم..."
                className="flex-1 bg-transparent text-white text-sm outline-none p-2 placeholder-gray-400 w-full"
                disabled={isLoading}
                maxLength={500}
              />
              <button
                onClick={handleSend}
                disabled={!inputValue.trim() || isLoading}
                className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-lg bg-[#8B5CF6] text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:bg-[#C084FC] mr-2"
              >
                <svg className="w-5 h-5 -rotate-90 transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19V5m-7 7l7-7 7 7"></path></svg>
              </button>
            </div>
            <div className="text-left mt-1 px-2 flex justify-end">
              <span className="text-[10px] text-gray-400">{inputValue.length}/500</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SaraChatWidget;
